/**
 * Driver notification preferences (Profile → Notifications), kept on this
 * phone (InGo, 2 Oct 2026). The old system kept them in its own database
 * for its own sender; InGo's server sends to every phone registered, so
 * "push when the app is closed" is honoured by registering this phone or
 * not (`driverPush.js`): off takes it off InGo's list, on puts it back.
 */

const CACHE_PREFIX = 'ingo_driver_notif_prefs:';

/** @typedef {{ new_offers: boolean, offer_sound: boolean, push_when_closed: boolean }} DriverNotifPrefs */

export const DEFAULT_DRIVER_NOTIF_PREFS = /** @type {DriverNotifPrefs} */ ({
  new_offers: true,
  offer_sound: true,
  // Matches the database default (true). It was false here until 2026-09-06,
  // and the sender treated a saved false as "never push to this driver".
  push_when_closed: true,
});

function cacheKey(driverId) {
  return `${CACHE_PREFIX}${String(driverId || '').trim()}`;
}

/**
 * @param {unknown} raw
 * @returns {DriverNotifPrefs}
 */
export function normalizeDriverNotifPrefs(raw) {
  const base = { ...DEFAULT_DRIVER_NOTIF_PREFS };
  if (!raw || typeof raw !== 'object') return base;
  const o = /** @type {Record<string, unknown>} */ (raw);
  if (typeof o.new_offers === 'boolean') base.new_offers = o.new_offers;
  if (typeof o.offer_sound === 'boolean') base.offer_sound = o.offer_sound;
  if (typeof o.push_when_closed === 'boolean') base.push_when_closed = o.push_when_closed;
  // Sound / push only apply when new offers are on
  if (!base.new_offers) {
    base.offer_sound = false;
    base.push_when_closed = false;
  }
  return base;
}

/**
 * @param {string | null | undefined} driverId
 * @returns {DriverNotifPrefs}
 */
export function readCachedDriverNotifPrefs(driverId) {
  const id = String(driverId || '').trim();
  if (!id) return { ...DEFAULT_DRIVER_NOTIF_PREFS };
  try {
    const raw = localStorage.getItem(cacheKey(id));
    if (!raw) return { ...DEFAULT_DRIVER_NOTIF_PREFS };
    return normalizeDriverNotifPrefs(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_DRIVER_NOTIF_PREFS };
  }
}

/**
 * @param {string | null | undefined} driverId
 * @param {DriverNotifPrefs} prefs
 */
export function writeCachedDriverNotifPrefs(driverId, prefs) {
  const id = String(driverId || '').trim();
  if (!id) return;
  try {
    localStorage.setItem(cacheKey(id), JSON.stringify(normalizeDriverNotifPrefs(prefs)));
  } catch {
    // ignore
  }
}

/**
 * The rider's preferences on this phone.
 *
 * @param {string | null | undefined} driverId
 * @returns {Promise<{ prefs: DriverNotifPrefs, error?: string }>}
 */
export async function fetchDriverNotifPrefs(driverId) {
  return { prefs: readCachedDriverNotifPrefs(driverId) };
}

/**
 * Saves the rider's preferences on this phone. Turning "push when closed" off
 * or on takes this phone off InGo's push list or puts it back.
 *
 * @param {string | null | undefined} driverId
 * @param {Partial<DriverNotifPrefs>} patch
 * @returns {Promise<{ prefs: DriverNotifPrefs, error?: string }>}
 */
export async function saveDriverNotifPrefs(driverId, patch) {
  const id = String(driverId || '').trim();
  const before = readCachedDriverNotifPrefs(id);
  const next = normalizeDriverNotifPrefs({
    ...before,
    ...(patch && typeof patch === 'object' ? patch : {}),
  });
  writeCachedDriverNotifPrefs(id, next);

  if (id && before.push_when_closed !== next.push_when_closed) {
    try {
      const push = await import('./driverPush');
      if (next.push_when_closed) await push.registerDriverPushToken(id);
      else await push.forgetThisPhone();
    } catch (e) {
      return { prefs: next, error: e?.message || 'This phone could not be updated for push. Try again.' };
    }
  }

  return { prefs: next };
}
